import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, Card, EmptyState, Mono, PageBody, PageHeader } from "../ui/Field";

type FormSummary = { block_id: string; page_id: string | null; n: number; last_at: number; failures: number };

export default function Forms() {
  const { slug } = useParams();
  const [items, setItems] = useState<FormSummary[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    api.get<{ items: FormSummary[] }>(`/api/sites/${slug}/forms`)
      .then((r) => setItems(r.items))
      .catch((e) => setErr((e as Error).message));
  }, [slug]);

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 06 · Inbox"
        title={<>Forms</>}
        description={<>One row per <Mono>form.builder</Mono> block with submissions. Click a row to inspect, replay, or audit failures.</>}
      />
      {err && <Banner kind="error">{err}</Banner>}

      <Card number="F1" title={`Active forms (${items.length})`} bodyClassName={items.length === 0 ? "p-5" : "p-0"}>
        {items.length === 0 ? (
          <EmptyState
            title="No submissions yet."
            description={<>Drop a <Mono>form.builder</Mono> block on a published page; submissions will collect here.</>}
          />
        ) : (
          <table className="w-full text-[13.5px] border-collapse">
            <thead>
              <tr className="text-left label-tracked border-b border-[var(--color-rule)]">
                <th className="px-5 py-2.5">Form</th>
                <th className="px-5 py-2.5">Submissions</th>
                <th className="px-5 py-2.5">Failures</th>
                <th className="px-5 py-2.5">Last submission</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.block_id} className="border-b border-[var(--color-rule)] last:border-0 hover:bg-[var(--color-paper-2)] transition-colors">
                  <td className="px-5 py-2.5">
                    <Link to={`/sites/${slug}/forms/${s.block_id}`} className="text-[var(--color-accent)] no-underline hover:underline">
                      <Mono>{s.block_id.slice(-10)}</Mono>
                    </Link>
                  </td>
                  <td className="px-5 py-2.5">{s.n}</td>
                  <td className="px-5 py-2.5">{s.failures > 0 ? <Badge tone="error">{s.failures}</Badge> : <span className="text-[var(--color-muted)]">—</span>}</td>
                  <td className="px-5 py-2.5 text-[12px] text-[var(--color-muted)]">{new Date(s.last_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </PageBody>
  );
}
